const AppPage = require('@/lib/Navigation/AppPage');
const Branch = require('@/lib/Branches/Branch');
const DeviceGroup = require('../DeviceGroups/DeviceGroup');
const { fromJson, toBool, toDate, toFloat, toInt, toNullableBool, toNullableInt, toNullableString } = require('@/functions/transform');

/**
 * @constructor
 */
const Device = function () {
    /**
     * @type {number|null}
     */
    this.ID = null;

    /**
     * @type {number|null}
     */
    this.autoLockAfterMinutes = null;

    /**
     * Automatically start a Dojo/Verifone/Worldpay transaction when switching to that payment tab?
     * Can be set via the Back Office.
     * This is useful to disable if the client makes a lot of Cardholder Not Present or partial payments from
     * this device, to avoid having to cancel the auto started transaction to make changes.
     *
     * @type {boolean}
     */
    this.autoStartCardTransactions = true;

    this.name = '';
    this.guid = null;
    this.guidAssigned = '';

    /**
     * @type {Branch|null}
     */
    this.defaultBranch = null;

    this.defaultFloat = 0;

    /**
     * @type {number[]}
     */
    this.disabledPaymentMethodIDs = [];

    /**
     * The minimum amount of cash that should be in a till.
     * Not currently used.
     *
     * @type {float}
     */
    this.cashLowLimit = 0;

    /**
     * The maximum amount of cash that should be in a till.
     * Not currently used.
     *
     * @type {float}
     */
    this.cashHighLimit = 0;

    /**
     * The maximum number of cheques or vouchers that should be in the till.
     * Not currently used.
     *
     * @type {number}
     */
    this.paperLimit = 0;

    /**
     * @type {string|null}
     */
    this.eposLandingPageKey = null;

    /**
     * @type {AppPage|null}
     */
    this.landingPage = null;

    this.extendedTradingSessionSummaries = true;

    /**
     * @type {object|null}
     */
    this.paymentTerminalConfig = null;

    /**
     * @type {string|null}
     */
    this.paymentTerminalRef = null;

    this.terminalHasPrinter = true;

    /**
     * @type {boolean}
     */
    this.forceOrdersOnline = false;

    this.currentTradingSessionID = null;
    this.focusOnSearch = false;
    this.eposPrintReceipts = 'always';

    /**
     * Device has a souvenir ticket printer?
     * This affects which template is used when printing tickets.
     *
     * @type {boolean}
     */
    this.printTickets = false;

    /**
     * @type {boolean}
     */
    this.autoPrintTicketsOnRedemption = false;

    /**
     * @type {boolean}
     */
    this.autoPrintTicketsOnSale = false;

    /**
     * Controls if the till/epos prints kitchen receipts when a kitchen order is created irrespective of whether it is
     * created by Send To Kitchen or clicking Pay.
     * Note: auto printing receipts on the kitchen screen is controlled by the branches.kitchenAutoPrint setting.
     *
     * @type {boolean}
     */
    this.autoPrintKitchenReceipts = false;

    /**
     * JSON encoded printer configuration.
     * This should always be a string not an object. It gets passed to the PrintRouter which will decode it.
     *
     * @type {?string}
     */
    this.printConfig = null;

    /**
     * Is there a touch sensitive customer facing screen enabled on this device?
     *
     * @type {boolean}
     */
    this.customerTouchScreenEnabled = false;

    /**
     * Should the functionality to manage group bookings be available?
     *
     * @type {boolean}
     */
    this.manageGroupBookings = false;

    /**
     * Device can either have only one Device Group or nothing.
     *
     * @type {DeviceGroup|null}
     */
    this.deviceGroup = null;

    /**
     * Should the RedeemOrderButton vue component be enabled and visible on the page in the Ticket summary?
     *
     * @type {boolean}
     */
    this.showRedeemOrderButton = true;
};

Device.prototype = {
    getHydrationMap() {
        return {
            autoPrintKitchenReceipts: toBool,
            autoPrintTicketsOnRedemption: toBool,
            autoPrintTicketsOnSale: toBool,
            autoLockAfterMinutes: toNullableInt,
            autoStartCardTransactions: toBool,
            cashHighLimit: toFloat,
            cashLowLimit: toFloat,
            currentTradingSessionID: toNullableInt,
            defaultBranch: {
                model: Branch,
            },
            defaultFloat: toFloat,
            disabledPaymentMethodIDs: {},
            eposLandingPageKey: {
                field: function (data) {
                    // Re-hydration.
                    if (data.eposLandingPageKey) {
                        return data.eposLandingPageKey;
                    }
                    // Raw data from the API.
                    if (data.eposlandingpage && data.eposlandingpage.key) {
                        return data.eposlandingpage.key;
                    }
                    return null;
                },
            },
            eposPrintReceipts: toNullableString,
            extendedTradingSessionSummaries: toBool,
            focusOnSearch: toNullableBool,
            forceOrdersOnline: toBool,
            guid: {},
            guidAssigned: {
                field: ['guidChangedAt', 'guidAssigned'],
                transform: toDate,
            },
            ID: {
                field: ['deviceID', 'ID'],
                transform: toInt,
            },
            manageGroupBookings: toBool,
            name: {},
            paperLimit: toInt,
            paymentTerminalConfig: fromJson,
            paymentTerminalRef: toNullableString,
            printConfig: toNullableString,
            printTickets: toBool,
            terminalHasPrinter: toBool,
            customerTouchScreenEnabled: toBool,
            showRedeemOrderButton: toBool,

            deviceGroup: {
                field: (data) => {
                    const { deviceGroup, devicegroups } = data;
                    // Re-hydration.
                    if (deviceGroup) {
                        return deviceGroup;
                    }
                    // Raw data from the API.
                    return devicegroups?.[0] ?? null;
                },
                model: DeviceGroup,
            },
        };
    },

    afterHydration() {
        if (this.eposLandingPageKey) {
            this.landingPage = AppPage.fromKey(this.eposLandingPageKey);
        }
    },

    autoPrintReceipts() {
        return this.eposPrintReceipts === 'always';
    },

    getPaymentTerminalConfigForMethod(paymentMethod) {
        if (this.paymentTerminalConfig && this.paymentTerminalConfig[paymentMethod]) {
            return this.paymentTerminalConfig[paymentMethod];
        }

        return null;
    },
};

module.exports = Device;
